import { vec3, vec4, vec2 } from "gl-matrix";
import * as pako from 'pako';
import { isBrowser, isNode } from 'browser-or-node';

declare var TextEncoder: any;
declare var TextDecoder: any;
declare function require(moduleName: string): any;

let _TextDecoder: () => any; // new TextDecoder();
let _TextEncoder: () => any; // new TextEncoder();

if (isNode) {
    // let hack = require; // system.js searches code for require('blah')...
    // let util = hack('util');
    // _TextDecoder = () => new util.TextDecoder();
    // _TextEncoder = () => new util.TextEncoder();
    //import { TextEncoder, TextDecoder } from "util";
} else {
    _TextDecoder = () => new TextDecoder();
    _TextEncoder = () => new TextEncoder();
}

export class SerializationUtil {
    public view: DataView;
    public littleEndian: boolean;
    public offset: number;

    protected textEncoder: any; // TextEncoder;
    protected textDecoder: any; // TextDecoder;

    constructor() {
        this.littleEndian = false;
        this.offset = 0;
        this.textDecoder = _TextDecoder();
        this.textEncoder = _TextEncoder();
    }

    public writeTo(view: DataView): SerializationUtil {
        this.offset = 0;
        this.view = view;
        return this;
    }

    public readFrom(data: Uint8Array, compressed: boolean): SerializationUtil {
        this.offset = 0;
        let infl = compressed ? pako.inflate(data) : data;
        this.view = new DataView(infl.buffer);
        return this;
    }

    public finalize(compress: boolean): Uint8Array {
        let data = new Uint8Array(this.view.buffer, 0, this.offset);
        return compress ? pako.deflate(data) : data;
    }

    public writeNumericEnum<T extends number>(value: T, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setUint32(this.offset, value, this.littleEndian);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
        });
    }

    public readNumericEnum<T extends number>(nullable?: boolean): T {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getUint32(this.offset);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;

            return value as T;
        });
    }

    public writeVaryingObjectArray<T>(data: T[], writeCallback: (obj: T) => void, nullable?: boolean): SerializationUtil {
        return this.writeVaryingLengthData(data, () => {
            for (let i = 0; i < data.length; i++) {
                this.writeNullableObject<T>(data[i], nullable, writeCallback);
            }
        });
    }

    public readVaryingObjectArray<T>(readCallback: () => T, nullable?: boolean): T[] {
        return this.readyVaryingLengthData(Array, (length) => {
            let result = new Array<T>(length);
            for (let i = 0; i < length; i++) {
                result[i] = this.readNullableObject(nullable, readCallback);
            }
            return result;
        });
    }

    public writeFixedObjectArray<T>(data: T[], writeCallback: (obj: T) => void, nullable?: boolean): SerializationUtil {
        this.checkFixedArray(data);
        for (let i = 0; i < data.length; i++) {
            this.writeNullableObject<T>(data[i], nullable, writeCallback);
        }
        return this;
    }

    public readFixedObjectsArray<T>(length: number, readCallback: () => T, nullable?: boolean): T[] {
        let result = new Array<T>(length);
        for (let i = 0; i < length; i++) {
            result[i] = this.readNullableObject(nullable, readCallback);
        }
        return result;
    }

    public writeVec4(vec: vec4 | number[]): SerializationUtil {
        return this.writeFixedFloat32Array(vec);
    }

    public readVec4(): vec4 {
        return this.readFixedFloat32Array(4) as vec4;
    }

    public writeVec3(vec: vec3 | number[]): SerializationUtil {
        return this.writeFixedFloat32Array(vec);
    }

    public readVec3(): vec3 {
        return this.readFixedFloat32Array(3) as vec3;
    }

    public writeVec2(vec: vec2 | number[]): SerializationUtil {
        return this.writeFixedFloat32Array(vec);
    }

    public readVec2(): vec2 {
        return this.readFixedFloat32Array(2) as vec2;
    }

    public writeFixedFloat64Array(data: Float64Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setFloat64(this.offset, data[i], this.littleEndian);
            this.offset += Float64Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedFloat64Array(length: number): Float64Array {
        let value = new Float64Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readFloat64(false);
        }
        return value;
    }

    public writeVaryingFloat64Array(data: Float64Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedFloat64Array(data));
    }

    public readVaryingFloat64Array(): Float64Array {
        return this.readyVaryingLengthData(Float64Array, (length) => this.readFixedFloat64Array(length));
    }

    public writeFixedFloat32Array(data: Float32Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setFloat32(this.offset, data[i], this.littleEndian);
            this.offset += Float32Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedFloat32Array(length: number): Float32Array {
        let value = new Float32Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readFloat32(false);
        }
        return value;
    }

    public writeVaryingFloat32Array(data: Float32Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedFloat32Array(data));
    }

    public readVaryingFloat32Array(): Float32Array {
        return this.readyVaryingLengthData(Float32Array, (length) => this.readFixedFloat32Array(length));
    }

    public writeFixedUint32Array(data: Uint32Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setUint32(this.offset, data[i], this.littleEndian);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedUint32Array(length: number): Uint32Array {
        let value = new Uint32Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readUint32(false);
        }
        return value;
    }

    public writeVaryingUint32Array(data: Uint32Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedUint32Array(data));
    }

    public readVaryingUint32Array(): Uint32Array {
        return this.readyVaryingLengthData(Uint32Array, (length) => this.readFixedUint32Array(length));
    }

    public writeFixedUint16Array(data: Uint16Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setUint16(this.offset, data[i], this.littleEndian);
            this.offset += Uint16Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedUint16Array(length: number): Uint16Array {
        let value = new Uint16Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readUint16(false);
        }
        return value;
    }

    public writeVaryingUint16Array(data: Uint16Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedUint16Array(data));
    }

    public readVaryingUint16Array(): Uint16Array {
        return this.readyVaryingLengthData(Uint16Array, (length) => this.readFixedUint16Array(length));
    }

    public writeFixedUint8Array(data: Uint8Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setUint8(this.offset, data[i]);
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedUint8Array(length: number): Uint8Array {
        let value = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readUint8(false);
        }
        return value;
    }

    public writeVaryingUint8Array(data: Uint8Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedUint8Array(data));
    }

    public readVaryingUint8Array(): Uint8Array {
        return this.readyVaryingLengthData(Uint8Array, (length) => this.readFixedUint8Array(length));
    }

    public writeFixedInt32Array(data: Int32Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setInt32(this.offset, data[i], this.littleEndian);
            this.offset += Int32Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedInt32Array(length: number): Int32Array {
        let value = new Int32Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readInt32(false);
        }
        return value;
    }

    public writeVaryingInt32Array(data: Int32Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedInt32Array(data));
    }

    public readVaryingInt32Array(): Int32Array {
        return this.readyVaryingLengthData(Int32Array, (length) => this.readFixedInt32Array(length));
    }

    public writeFixedInt16Array(data: Int16Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setInt16(this.offset, data[i], this.littleEndian);
            this.offset += Int16Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedInt16Array(length: number): Int16Array {
        let value = new Int16Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readInt16(false);
        }
        return value;
    }

    public writeVaryingInt16Array(data: Int16Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedInt16Array(data));
    }

    public readVaryingInt16Array(): Int16Array {
        return this.readyVaryingLengthData(Int16Array, (length) => this.readFixedInt16Array(length));
    }

    public writeFixedInt8Array(data: Int8Array | number[]): SerializationUtil {
        this.checkFixedArray(data);

        for (let i = 0; i < data.length; i++) {
            this.view.setInt8(this.offset, data[i]);
            this.offset += Int8Array.BYTES_PER_ELEMENT;
        }
        return this;
    }

    public readFixedInt8Array(length: number): Int8Array {
        let value = new Int8Array(length);
        for (let i = 0; i < length; i++) {
            value[i] = this.readInt8(false);
        }
        return value;
    }

    public writeVaryingInt8Array(data: Int8Array | number[]): SerializationUtil {
        return this.writeVaryingLengthData(data, () => this.writeFixedInt8Array(data));
    }

    public readVaryingInt8Array(): Int8Array {
        return this.readyVaryingLengthData(Int8Array, (length) => this.readFixedInt8Array(length));
    }

    public writeFloat64(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0.0, () => {
            this.view.setFloat64(this.offset, value, this.littleEndian);
            this.offset += Float64Array.BYTES_PER_ELEMENT;
        });
    }

    public readFloat64(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0.0, () => {
            let value = this.view.getFloat64(this.offset);
            this.offset += Float64Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeFloat32(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0.0, () => {
            this.view.setFloat32(this.offset, value, this.littleEndian);
            this.offset += Float32Array.BYTES_PER_ELEMENT;
        });
    }

    public readFloat32(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0.0, () => {
            let value = this.view.getFloat32(this.offset);
            this.offset += Float32Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeUint32(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setUint32(this.offset, value, this.littleEndian);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;
        });
    }

    public readUint32(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getUint32(this.offset);
            this.offset += Uint32Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeUint16(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setUint16(this.offset, value, this.littleEndian);
            this.offset += Uint16Array.BYTES_PER_ELEMENT;
        });
    }

    public readUint16(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getUint16(this.offset);
            this.offset += Uint16Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeUint8(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setUint8(this.offset, value);
            this.offset += Uint8Array.BYTES_PER_ELEMENT;
        });
    }

    public readUint8(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getUint8(this.offset);
            this.offset += Uint8Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeInt32(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setInt32(this.offset, value, this.littleEndian);
            this.offset += Int32Array.BYTES_PER_ELEMENT;
        });
    }

    public readInt32(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getInt32(this.offset);
            this.offset += Int32Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeInt16(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setInt16(this.offset, value, this.littleEndian);
            this.offset += Int16Array.BYTES_PER_ELEMENT;
        });
    }

    public readInt16(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getInt16(this.offset);
            this.offset += Int16Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeInt8(value: number, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, 0, () => {
            this.view.setInt8(this.offset, value);
            this.offset += Int8Array.BYTES_PER_ELEMENT;
        });
    }

    public readInt8(nullable?: boolean): number {
        return this.readNullableValue(nullable, 0, () => {
            let value = this.view.getInt8(this.offset);
            this.offset += Int8Array.BYTES_PER_ELEMENT;

            return value;
        });
    }

    public writeBoolean(value: boolean, nullable?: boolean): SerializationUtil {
        return this.writeNullableValue(value, nullable, false, () => {
            this.writeUint8(value ? 1 : 0, false);
        });
    }

    public readBoolean(nullable?: boolean): boolean {
        return this.readNullableValue(nullable, false, () => {
            return this.readUint8(false) === 1;
        });
    }

    public writeStrings(strs: string[]): SerializationUtil {
        return this.writeVaryingLengthData(strs, () => {
            for (let str of strs) {
                this.writeString(str);
            }
        });
    }

    public readStrings(): string[] {
        return this.readyVaryingLengthData<string[]>(Array, (count) => {
            let strs = new Array<string>(count);
            for (let i = 0; i < count; i++) {
                strs[i] = this.readString();
            }

            return strs;
        });
    }

    public writeString(str: string): SerializationUtil {
        // Get the bytes, when string is null, the array is also null
        let bytes = str === null ? null : this.textEncoder.encode(str);

        return this.writeVaryingLengthData(bytes, () => this.writeFixedUint8Array(bytes));
    }

    public readString(): string {
        let storage: VaryingLengthStorageType = this.readUint8();
        if (storage == VaryingLengthStorageType.Null) {
            return null;
        } else if (storage == VaryingLengthStorageType.Empty) {
            return '';
        } else {
            return this.textDecoder.decode(this.readFixedUint8Array(this.readVaryingLengthStorageLength(storage)));
        }
    }

    public writeJSON(obj: any): SerializationUtil {
        return this.writeString(obj ? JSON.stringify(obj) : null);
    }

    public readJSON(): any {
        let str = this.readString();
        return str === null ? null : JSON.parse(str);
    }

    private readNullableObject<T>(nullable: boolean, dataRead: () => T): T {
        if (nullable) {
            let storage: NullableStorageType = this.readUint8();

            switch (storage) {
                case NullableStorageType.Null:
                    return null;
                case NullableStorageType.Data:
                    return dataRead();
            }

        } else {
            return dataRead();
        }
    }

    private writeNullableObject<T>(value: T, nullable: boolean, dataWrite: (data: T) => void): SerializationUtil {
        if (nullable) {
            if (value === null) {
                return this.writeUint8(NullableStorageType.Null);
            }
            else {
                this.writeUint8(NullableStorageType.Data);
                dataWrite(value);
                return this;
            }
        } else {
            if (value === null) {
                throw new Error(`Attempting to write null for an object that is not allowed to be null`);
            } else {
                dataWrite(value);
                return this;
            }
        }
    }

    private readNullableValue<T>(nullable: boolean, defaultValue: any, dataRead: () => T): T {
        if (nullable) {
            let storage: NullableStorageType = this.readUint8();

            switch (storage) {
                case NullableStorageType.Null:
                    return null;
                case NullableStorageType.Default:
                    return defaultValue;
                case NullableStorageType.True:
                    return true as any;
                case NullableStorageType.False:
                    return false as any;
                case NullableStorageType.Data:
                    return dataRead();
            }

        } else {
            return dataRead();
        }
    }

    private writeNullableValue(value: any, nullable: boolean, defaultValue: any, dataWrite: () => void): SerializationUtil {
        if (nullable) {
            if (value === null) {
                return this.writeUint8(NullableStorageType.Null);
            } else if (value === true) {
                return this.writeUint8(NullableStorageType.True);
            } else if (value === false) {
                return this.writeUint8(NullableStorageType.False);
            } else if (value === defaultValue) {
                return this.writeUint8(NullableStorageType.Default);
            }
            else {
                this.writeUint8(NullableStorageType.Data);
                dataWrite();
                return this;
            }
        } else {
            if (value === null) {
                throw new Error(`Attempting to write null for a non-nullable property`);
            } else {
                dataWrite();
                return this;
            }
        }
    }

    private readyVaryingLengthData<T>(ctor: any, dataRead: (length: number) => T): T {
        let storage: VaryingLengthStorageType = this.readUint8();
        if (storage == VaryingLengthStorageType.Null) {
            return null;
        } else if (storage == VaryingLengthStorageType.Empty) {
            return new ctor(0);
        } else {
            return dataRead(this.readVaryingLengthStorageLength(storage));
        }
    }

    private writeVaryingLengthData(arr: { length: number }, dataWrite: () => void): SerializationUtil {
        let storage = this.determineVaryingLengthStorage(arr);
        this.writeUint8(storage);
        switch (storage) {
            case VaryingLengthStorageType.LenUint8:
                this.writeUint8(arr.length);
                dataWrite();
                break;
            case VaryingLengthStorageType.LenUint16:
                this.writeUint16(arr.length);
                dataWrite();
                break;
            case VaryingLengthStorageType.LenUint32:
                this.writeUint32(arr.length);
                dataWrite();
                break;
        }

        return this;
    }

    private determineVaryingLengthStorage(arr: { length: number }): VaryingLengthStorageType {
        if (arr === null) {
            return VaryingLengthStorageType.Null;
        } else if (arr.length === 0) {
            return VaryingLengthStorageType.Empty;
        } else {
            if (arr.length <= 0xFF) {
                return VaryingLengthStorageType.LenUint8;
            } else if (arr.length <= 0xFFFF) {
                return VaryingLengthStorageType.LenUint16;
            } else {
                return VaryingLengthStorageType.LenUint32;
            }
        }
    }

    private readVaryingLengthStorageLength(storage: VaryingLengthStorageType): number {
        switch (storage) {
            case VaryingLengthStorageType.LenUint8:
                return this.readUint8();
            case VaryingLengthStorageType.LenUint16:
                return this.readUint16();
            case VaryingLengthStorageType.LenUint32:
                return this.readUint32();
        }

        throw new Error(`No length can be determined for storage type ${storage}`);
    }

    private checkFixedArray(data: any) {
        if (!data) {
            throw new Error(`Fixed array data cannot be null. If the array is nullable use the writeVarying*Array methods instead.`);
        }
    }
}

enum VaryingLengthStorageType {
    Null = 0,
    Empty = 1,
    LenUint8 = 2,
    LenUint16 = 3,
    LenUint32 = 4
}

enum NullableStorageType {
    Null = 0,
    Default = 1,
    True = 2,
    False = 3,
    Data = 4
}